import { DataType } from "@/types/enums/DataType";
import type { Field } from "@/types/Field";

/**
 * Which `{{…}}` markers an SMS body may carry (plan decision 6). The same
 * allowlist for both recipient modes:
 *  - {@link SMS_PLACEHOLDER_ALLOWLIST} — guest columns and event facts, by
 *    exact name;
 *  - `additionalFields.<name>` — a custom GUEST field of THIS event whose type
 *    is not IMAGE (an image renders as an object key, never text).
 *
 * Everything else is refused at save time (backend 400
 * `smsPlaceholderNotAllowed`): the portal credentials (`guestId code username
 * password credentialsQuery`), the bare `additionalFields` map, every minted
 * link, an unknown key — and a marker spelled with spaces or another case
 * (`{{ firstName }}`), which the renderer would not resolve and would strip.
 * `salutation` left the list in wave D (C3) in every repo — consumer render,
 * backend gate, this picker and the help.
 *
 * A marker is two opening braces, anything without a brace, two closing
 * braces; text that merely contains two opening braces is not one.
 *
 * MIRROR: main-backend `org/nikita/app/sms/SmsPlaceholders.java`
 * (`ALLOWED`, `firstDisallowed` — the save gate), main-consumer
 * `executor/send-sms.ts` (render: allowlist pick + non-IMAGE
 * `additionalFields`, unknown markers stripped). One list in three repos.
 */
export const SMS_PLACEHOLDER_ALLOWLIST = [
  "firstName",
  "lastName",
  "title",
  "company",
  "email",
  "phone",
  "mobile",
  "language",
  "eventName",
  "eventStart",
  "eventEnd",
  "eventLocation",
  "companyName",
] as const;

export type SmsPlaceholderKey = (typeof SMS_PLACEHOLDER_ALLOWLIST)[number];

/** The markers that name the EVENT (the same value for every guest) — MIRROR main-consumer `SMS_EVENT_PLACEHOLDERS`. */
export const SMS_EVENT_PLACEHOLDERS = ["eventName", "eventStart", "eventEnd", "eventLocation", "companyName"] as const;

export type SmsEventPlaceholder = (typeof SMS_EVENT_PLACEHOLDERS)[number];

const ALLOWED: ReadonlySet<string> = new Set(SMS_PLACEHOLDER_ALLOWLIST);

export const SMS_CUSTOM_PREFIX = "additionalFields.";

/** The default body a new SEND_SMS starts with (plan decision 6). */
export const SMS_DEFAULT_BODY = "{{eventName}}: ";

/** One marker: two opening braces, no brace, two closing braces. Group 1 is the key exactly as written. */
const MARKER = /\{\{([^{}]*)\}\}/g;

/** Names of the event's custom GUEST fields an SMS may name — every non-IMAGE one. */
export function smsCustomFieldNames(fields: readonly Field[]): ReadonlySet<string> {
  return new Set(
    fields
      .filter((f) => f.additionalField === true && f.dataType !== DataType.IMAGE)
      .map((f) => f.name)
      .filter((n): n is string => typeof n === "string" && n.length > 0),
  );
}

/**
 * May an SMS name this marker key? `customFields` undefined = the field list is
 * not known here: any non-empty `additionalFields.<name>` passes and the
 * backend has the final word. A known list is checked by name.
 */
export function isAllowedSmsPlaceholder(
  key: string,
  customFields?: ReadonlySet<string>,
): boolean {
  if (ALLOWED.has(key)) return true;
  if (!key.startsWith(SMS_CUSTOM_PREFIX)) return false;
  const name = key.slice(SMS_CUSTOM_PREFIX.length);
  if (name.length === 0) return false;
  return customFields === undefined || customFields.has(name);
}

/**
 * Every marker of `body` the SMS may not carry, as written without the
 * braces, distinct, in order of first appearance. Empty when every marker is
 * allowed.
 */
export function findDisallowedMarkers(
  body: string,
  customFields?: ReadonlySet<string>,
): string[] {
  if (typeof body !== "string" || body.length === 0) return [];
  const out: string[] = [];
  for (const m of body.matchAll(MARKER)) {
    const key = m[1];
    if (isAllowedSmsPlaceholder(key, customFields)) continue;
    if (!out.includes(key)) out.push(key);
  }
  return out;
}

/** Does the body carry at least one `{{…}}` marker? */
export function hasSmsMarker(body: string): boolean {
  return new RegExp(MARKER.source).test(body);
}

/**
 * The body with every event marker (`{{eventName}}` …, exactly as written)
 * replaced by the event's own value, for the part counter (audit r1 U3). A
 * marker without a value, and every guest marker, stays as typed. One pass,
 * never re-scanned — like the sender.
 */
export function fillSmsEventMarkers(
  body: string,
  values: Partial<Record<SmsEventPlaceholder, string>>,
): string {
  return body.replace(MARKER, (marker, key: string) => {
    const value = (values as Record<string, string | undefined>)[key];
    return typeof value === "string" ? value : marker;
  });
}
